import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Shield, Users, FileText, Flag, Megaphone, Wallet,
  Check, X, Ban, UserCog, Search, ChevronRight, AlertTriangle, Eye, MessageCircle, Phone, Mail, MapPin, Calendar, ChevronDown, ChevronUp,
  TrendingUp, Package, Clock, Sparkles, Heart, BarChart3, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat card component
const StatCard = ({ icon: Icon, label, value, color = 'emerald', subtext }) => (
  <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 p-5 shadow-sm">
    <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center mb-3`}>
      <Icon className={`h-5 w-5 text-${color}-700 dark:text-${color}-400`} />
    </div>
    <p className="text-2xl font-bold text-stone-900 dark:text-white">{value}</p>
    <p className="text-sm text-stone-500 dark:text-stone-400">{label}</p>
    {subtext && <p className="text-xs text-stone-400 mt-1">{subtext}</p>}
  </div>
);

// Mini bar chart
const MiniBarChart = ({ data, valueKey = 'count', color = 'emerald', height = 80 }) => {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className={`w-full rounded-t bg-${color}-500 dark:bg-${color}-600 transition-all duration-300`}
            style={{ height: `${Math.max((d[valueKey] / maxVal) * 100, 4)}%` }}
            title={`${d.date}: ${d[valueKey]}`} />
          {i % 2 === 0 && <span className="text-[9px] text-stone-400 whitespace-nowrap">{d.date}</span>}
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [bannerRequests, setBannerRequests] = useState([]);
  const [bannersTotal, setBannersTotal] = useState(0);
  const [fundData, setFundData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportFilter, setReportFilter] = useState('pending');
  const [distributeDialog, setDistributeDialog] = useState(false);
  const [distributePostId, setDistributePostId] = useState('');
  const [distributeAmount, setDistributeAmount] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/feed');
      return;
    }
    fetchAll();
  }, [user, authLoading]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchReports(), fetchUsers(), fetchBanners(), fetchFund()]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard`, { headers });
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/reports?status=${reportFilter}`, { headers });
      setReports(res.data.reports);
      setReportsTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const res = await axios.get(`${API_URL}/api/admin/users${params}`, { headers });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch { /* ignore */ }
  }, [searchQuery]);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/banners?status=pending`, { headers });
      setBannerRequests(res.data.requests);
      setBannersTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const fetchFund = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/fund`, { headers });
      setFundData(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchReports(); }, [reportFilter]);
  useEffect(() => { fetchUsers(); }, [searchQuery, fetchUsers]);

  const handleReport = async (reportId, action) => {
    try {
      await axios.put(`${API_URL}/api/admin/reports/${reportId}`, { action }, { headers });
      toast.success(action === 'approve' ? 'Segnalazione approvata' : 'Segnalazione ignorata');
      fetchReports();
      fetchStats();
    } catch { toast.error('Errore'); }
  };

  const handleBanner = async (requestId, action) => {
    try {
      await axios.put(`${API_URL}/api/admin/banners/${requestId}`, { action }, { headers });
      toast.success(action === 'approve' ? 'Banner approvato' : 'Banner rifiutato');
      fetchBanners();
    } catch { toast.error('Errore'); }
  };

  const handleBanUser = async (userId, banned) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/ban`, { banned }, { headers });
      toast.success(banned ? 'Utente bannato' : 'Ban rimosso');
      fetchUsers();
    } catch { toast.error('Errore'); }
  };

  const handleVerifyUser = async (userId, verified) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/verify`, { verified }, { headers });
      toast.success(verified ? 'Utente verificato' : 'Verifica rimossa');
      fetchUsers();
    } catch { toast.error('Errore'); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role }, { headers });
      toast.success('Ruolo aggiornato');
      fetchUsers();
    } catch { toast.error('Errore'); }
  };

  const handleAdminChat = async (userId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/chat/${userId}`, {}, { headers });
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const [expandedUser, setExpandedUser] = useState(null);

  const handleDistribute = async () => {
    if (!distributePostId || !distributeAmount) return;
    try {
      await axios.post(`${API_URL}/api/admin/fund/distribute`, {
        post_id: distributePostId, amount: parseFloat(distributeAmount)
      }, { headers });
      toast.success('Fondi distribuiti con successo!');
      setDistributeDialog(false);
      setDistributePostId('');
      setDistributeAmount('');
      fetchFund();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </div>
    );
  }

  const t = (it, en) => language === 'it' ? it : en;

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-emerald-800 flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="admin-title">
              {t('Dashboard Admin', 'Admin Dashboard')}
            </h1>
            <p className="text-stone-500">{t('Gestione piattaforma Moneyfor', 'Moneyfor platform management')}</p>
          </div>
        </div>

        {/* Stats Grid - Row 1: Main metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatCard icon={Users} label={t('Utenti totali', 'Total users')} value={stats.total_users}
            subtext={`+${stats.new_users_7d || 0} ${t('ultimi 7gg', 'last 7d')}`} />
          <StatCard icon={Wallet} label={t('Totale raccolto', 'Total raised')} value={`€${(stats.total_raised || 0).toFixed(0)}`}
            subtext={`€${(stats.raised_30d || 0).toFixed(0)} ${t('ultimi 30gg', 'last 30d')}`} color="amber" />
          <StatCard icon={Heart} label={t('Donazioni', 'Donations')} value={stats.total_donations || 0}
            subtext={`${stats.donations_count_7d || 0} ${t('ultimi 7gg', 'last 7d')}`} color="rose" />
          <StatCard icon={Flag} label={t('Segnalazioni', 'Reports')} value={stats.pending_reports} color="red" />
        </div>
        {/* Stats Grid - Row 2: Sections */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={FileText} label={t('Post attivi', 'Active posts')} value={stats.active_posts}
            subtext={`+${stats.new_posts_7d || 0} ${t('ultimi 7gg', 'last 7d')}`} color="blue" />
          <StatCard icon={Package} label={t('Mercatino', 'Marketplace')} value={stats.total_marketplace || 0} color="teal" />
          <StatCard icon={Clock} label={t('Dona Tempo', 'Donate Time')} value={stats.total_time_offers || 0} color="violet" />
          <StatCard icon={Sparkles} label={t('Desideri', 'Wishes')} value={stats.total_wishes || 0} color="pink" />
          <StatCard icon={Megaphone} label={t('Aziende', 'Companies')} value={stats.total_companies || 0} color="orange" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="stats" className="w-full">
          <TabsList className="w-full justify-start bg-white rounded-xl p-1 mb-6 overflow-x-auto flex-nowrap">
            <TabsTrigger value="stats" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white whitespace-nowrap" data-testid="tab-stats">
              <BarChart3 className="h-4 w-4 mr-2" />{t('Statistiche', 'Statistics')}
            </TabsTrigger>
            <TabsTrigger value="reports" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white whitespace-nowrap" data-testid="tab-reports">
              <Flag className="h-4 w-4 mr-2" />{t('Segnalazioni', 'Reports')}
              {stats.pending_reports > 0 && <Badge className="ml-2 bg-red-500 text-white text-xs">{stats.pending_reports}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white whitespace-nowrap" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />{t('Utenti', 'Users')}
            </TabsTrigger>
            <TabsTrigger value="banners" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white whitespace-nowrap" data-testid="tab-banners">
              <Megaphone className="h-4 w-4 mr-2" />{t('Banner', 'Banners')}
              {stats.pending_banners > 0 && <Badge className="ml-2 bg-amber-500 text-white text-xs">{stats.pending_banners}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="fund" className="rounded-lg data-[state=active]:bg-emerald-800 data-[state=active]:text-white whitespace-nowrap" data-testid="tab-fund">
              <Wallet className="h-4 w-4 mr-2" />{t('Fondo', 'Fund')}
            </TabsTrigger>
          </TabsList>


          {/* STATISTICS TAB */}
          <TabsContent value="stats">
            <div className="grid md:grid-cols-2 gap-6">
              {/* New Users Chart */}
              <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-stone-900 dark:text-white">{t('Nuovi utenti', 'New Users')}</h3>
                    <p className="text-sm text-stone-500">{t('Ultimi 14 giorni', 'Last 14 days')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-700">+{stats.new_users_30d || 0}</p>
                    <p className="text-xs text-stone-400">{t('ultimi 30gg', 'last 30d')}</p>
                  </div>
                </div>
                {stats.daily_users?.length > 0 && (
                  <MiniBarChart data={stats.daily_users} valueKey="count" color="emerald" height={100} />
                )}
              </div>

              {/* Donations Chart */}
              <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-stone-900 dark:text-white">{t('Donazioni', 'Donations')}</h3>
                    <p className="text-sm text-stone-500">{t('Ultimi 14 giorni', 'Last 14 days')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-amber-600">€{(stats.raised_30d || 0).toFixed(0)}</p>
                    <p className="text-xs text-stone-400">{t('ultimi 30gg', 'last 30d')}</p>
                  </div>
                </div>
                {stats.daily_donations?.length > 0 && (
                  <MiniBarChart data={stats.daily_donations} valueKey="amount" color="amber" height={100} />
                )}
              </div>

              {/* Top Donors */}
              <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 p-6 shadow-sm">
                <h3 className="font-heading font-semibold text-stone-900 dark:text-white mb-4">{t('Top donatori', 'Top Donors')}</h3>
                {(stats.top_donors || []).length === 0 ? (
                  <p className="text-stone-400 text-sm text-center py-6">{t('Nessun donatore ancora', 'No donors yet')}</p>
                ) : (
                  <div className="space-y-3">
                    {stats.top_donors.map((donor, i) => (
                      <div key={donor.user_id} className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-stone-100 text-stone-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-stone-50 text-stone-500'
                        }`}>{i + 1}</span>
                        <img src={donor.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${donor.user_id}`}
                          alt="" className="h-8 w-8 rounded-full" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-900 dark:text-white text-sm truncate">{donor.name}</p>
                          <Badge className="text-[10px]" variant="secondary">{donor.badge_level}</Badge>
                        </div>
                        <span className="font-semibold text-emerald-700 text-sm">€{(donor.total_donated || 0).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Platform Breakdown */}
              <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 p-6 shadow-sm">
                <h3 className="font-heading font-semibold text-stone-900 dark:text-white mb-4">{t('Riepilogo piattaforma', 'Platform Summary')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-700 rounded-xl">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-sm text-stone-700 dark:text-stone-300">{t('Donatori', 'Donors')}</span>
                    </div>
                    <span className="font-semibold text-stone-900 dark:text-white">{stats.donors_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-700 rounded-xl">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="text-sm text-stone-700 dark:text-stone-300">{t('Richiedenti', 'Recipients')}</span>
                    </div>
                    <span className="font-semibold text-stone-900 dark:text-white">{stats.recipients_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-700 rounded-xl">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-sm text-stone-700 dark:text-stone-300">{t('Aziende', 'Companies')}</span>
                    </div>
                    <span className="font-semibold text-stone-900 dark:text-white">{stats.total_companies || 0}</span>
                  </div>
                  <div className="border-t border-stone-200 dark:border-stone-600 pt-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                        <p className="text-lg font-bold text-teal-700">{stats.total_marketplace || 0}</p>
                        <p className="text-[11px] text-teal-600">{t('Annunci mercatino', 'Marketplace')}</p>
                      </div>
                      <div className="text-center p-2 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                        <p className="text-lg font-bold text-violet-700">{stats.total_time_offers || 0}</p>
                        <p className="text-[11px] text-violet-600">{t('Offerte tempo', 'Time offers')}</p>
                      </div>
                      <div className="text-center p-2 bg-rose-50 dark:bg-rose-900/20 rounded-lg">
                        <p className="text-lg font-bold text-rose-700">{stats.total_wishes || 0}</p>
                        <p className="text-[11px] text-rose-600">{t('Desideri', 'Wishes')}</p>
                      </div>
                      <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <p className="text-lg font-bold text-amber-700">{stats.pending_banners || 0}</p>
                        <p className="text-[11px] text-amber-600">{t('Banner in attesa', 'Pending banners')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Recap */}
              <div className="md:col-span-2 bg-gradient-to-r from-emerald-700 to-emerald-800 rounded-2xl p-6 text-white">
                <h3 className="font-heading font-semibold text-lg mb-4">{t('Riepilogo attività recente', 'Recent Activity Recap')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <ArrowUpRight className="h-5 w-5 mx-auto mb-1 text-emerald-200" />
                    <p className="text-2xl font-bold">{stats.new_users_7d || 0}</p>
                    <p className="text-xs text-emerald-200">{t('Nuovi utenti (7gg)', 'New users (7d)')}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Heart className="h-5 w-5 mx-auto mb-1 text-emerald-200" />
                    <p className="text-2xl font-bold">{stats.donations_count_7d || 0}</p>
                    <p className="text-xs text-emerald-200">{t('Donazioni (7gg)', 'Donations (7d)')}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Wallet className="h-5 w-5 mx-auto mb-1 text-emerald-200" />
                    <p className="text-2xl font-bold">€{(stats.raised_7d || 0).toFixed(0)}</p>
                    <p className="text-xs text-emerald-200">{t('Raccolti (7gg)', 'Raised (7d)')}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <FileText className="h-5 w-5 mx-auto mb-1 text-emerald-200" />
                    <p className="text-2xl font-bold">{stats.new_posts_7d || 0}</p>
                    <p className="text-xs text-emerald-200">{t('Nuovi post (7gg)', 'New posts (7d)')}</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports">
            <div className="flex gap-2 mb-4">
              {['pending', 'resolved', 'dismissed', 'all'].map(f => (
                <Button key={f} size="sm" variant={reportFilter === f ? 'default' : 'outline'}
                  className={`rounded-full text-sm ${reportFilter === f ? 'bg-emerald-800 text-white' : ''}`}
                  onClick={() => setReportFilter(f)}>
                  {f === 'pending' ? t('In attesa', 'Pending') : f === 'resolved' ? t('Risolte', 'Resolved') : f === 'dismissed' ? t('Ignorate', 'Dismissed') : t('Tutte', 'All')}
                </Button>
              ))}
            </div>
            <div className="space-y-3">
              {reports.length === 0 ? (
                <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                  <Flag className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">{t('Nessuna segnalazione', 'No reports')}</p>
                </div>
              ) : (
                reports.map(report => (
                  <motion.div key={report.report_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border border-stone-200 p-5"
                    data-testid={`report-${report.report_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={report.type === 'chat' ? 'bg-blue-100 text-blue-700' : report.reason === 'scam' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {report.type === 'chat' ? 'Chat' : report.reason}
                          </Badge>
                          {report.type !== 'chat' && <Badge variant="outline" className="text-xs">{report.reason}</Badge>}
                          <Badge variant="secondary">{report.status}</Badge>
                        </div>
                        <p className="font-semibold text-stone-900 truncate">{report.post_title}</p>
                        <p className="text-sm text-stone-500">
                          {report.type === 'chat' ? t('Segnalato', 'Reported') : t('Autore', 'Author')}: {report.post_author} | {t('Segnalato da', 'Reported by')}: {report.reporter_name}
                        </p>
                        {report.description && <p className="text-sm text-stone-600 mt-1 italic">"{report.description}"</p>}
                      </div>
                      {report.status === 'pending' && (
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleReport(report.report_id, 'approve')}
                            data-testid={`approve-report-${report.report_id}`}>
                            <Ban className="h-4 w-4 mr-1" />
                            {report.type === 'chat' ? t('Approva', 'Approve') : t('Rimuovi post', 'Remove post')}
                          </Button>
                          <Button size="sm" variant="outline" className="text-stone-600"
                            onClick={() => handleReport(report.report_id, 'dismiss')}
                            data-testid={`dismiss-report-${report.report_id}`}>
                            <X className="h-4 w-4 mr-1" />{t('Ignora', 'Dismiss')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input data-testid="admin-user-search" placeholder={t('Cerca utente per nome o email...', 'Search user by name or email...')}
                  className="pl-10 h-12 rounded-xl" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <p className="text-sm text-stone-500 mt-2">{usersTotal} {t('utenti totali', 'total users')}</p>
            </div>
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.user_id} className="bg-white rounded-xl border border-stone-200 overflow-hidden"
                  data-testid={`admin-user-${u.user_id}`}>
                  <div className="p-4 flex items-center gap-4">
                    <a href={`/profile/${u.user_id}`} target="_blank" rel="noopener noreferrer">
                      <img src={u.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${u.user_id}`}
                        alt="" className="h-10 w-10 rounded-full hover:ring-2 hover:ring-amber-300 transition-all cursor-pointer" />
                    </a>
                    <div className="flex-1 min-w-0">
                      <a href={`/profile/${u.user_id}`} className="font-semibold text-stone-900 truncate block hover:text-amber-700 transition-colors">{u.name}</a>
                      <p className="text-sm text-stone-500 truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary">{u.user_type}</Badge>
                      <Badge className={u.role === 'admin' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}>
                        {u.role || 'user'}
                      </Badge>
                      {u.is_verified && <Badge className="bg-blue-100 text-blue-700" data-testid={`verified-badge-${u.user_id}`}>Verificato</Badge>}
                      {u.is_banned && <Badge className="bg-red-100 text-red-700">Banned</Badge>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {u.role !== 'admin' && (
                        <Button size="sm" variant="ghost" data-testid={`admin-chat-${u.user_id}`}
                          onClick={() => handleAdminChat(u.user_id)}
                          className="text-amber-600 hover:bg-amber-50" title={t('Chatta', 'Chat')}>
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost"
                        onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                        className="text-stone-400 hover:bg-stone-50" title={t('Dettagli', 'Details')}>
                        {expandedUser === u.user_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                      {u.role !== 'admin' && (
                        <Button size="sm" variant="ghost"
                          data-testid={`verify-user-${u.user_id}`}
                          className={u.is_verified ? 'text-blue-600 hover:bg-blue-50' : 'text-stone-400 hover:bg-stone-50'}
                          onClick={() => handleVerifyUser(u.user_id, !u.is_verified)}
                          title={u.is_verified ? 'Rimuovi verifica' : 'Verifica identità'}>
                          <Shield className="h-4 w-4" />
                        </Button>
                      )}
                      {u.role !== 'admin' && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleRoleChange(u.user_id, u.role === 'moderator' ? 'user' : 'moderator')}
                          title={u.role === 'moderator' ? 'Rimuovi moderatore' : 'Rendi moderatore'}>
                          <UserCog className="h-4 w-4" />
                        </Button>
                      )}
                      {u.role !== 'admin' && (
                        <Button size="sm" variant="ghost" className={u.is_banned ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}
                          onClick={() => handleBanUser(u.user_id, !u.is_banned)}>
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Expanded user details */}
                  {expandedUser === u.user_id && (
                    <div className="px-4 pb-4 pt-0 border-t border-stone-100 bg-stone-50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 text-sm">
                        {u.phone_number && (
                          <div className="flex items-center gap-1.5 text-stone-600">
                            <Phone className="h-3.5 w-3.5 text-stone-400" />{u.phone_number}
                          </div>
                        )}
                        {u.residence_city && (
                          <div className="flex items-center gap-1.5 text-stone-600">
                            <MapPin className="h-3.5 w-3.5 text-stone-400" />{u.residence_city}
                          </div>
                        )}
                        {u.created_at && (
                          <div className="flex items-center gap-1.5 text-stone-600">
                            <Calendar className="h-3.5 w-3.5 text-stone-400" />
                            {new Date(u.created_at).toLocaleDateString('it-IT')}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-stone-600">
                          <Mail className="h-3.5 w-3.5 text-stone-400" />{u.email}
                        </div>
                        {u.age && <div className="text-stone-600">{t('Età', 'Age')}: {u.age}</div>}
                        {u.gender && <div className="text-stone-600">{t('Genere', 'Gender')}: {u.gender}</div>}
                        {u.company_name && <div className="text-stone-600">{t('Azienda', 'Company')}: {u.company_name}</div>}
                        {u.vat_number && <div className="text-stone-600">P.IVA: {u.vat_number}</div>}
                        <div className="text-stone-600">{t('Donato', 'Donated')}: €{(u.total_donated || 0).toFixed(2)}</div>
                        <div className="text-stone-600">{t('Ricevuto', 'Received')}: €{(u.total_received || 0).toFixed(2)}</div>
                        {u.iban && <div className="text-stone-600 col-span-2">IBAN: {u.iban}</div>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* BANNERS TAB */}
          <TabsContent value="banners">
            <div className="space-y-3">
              {bannerRequests.length === 0 ? (
                <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                  <Megaphone className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">{t('Nessuna richiesta banner in attesa', 'No pending banner requests')}</p>
                </div>
              ) : (
                bannerRequests.map(req => (
                  <div key={req.request_id} className="bg-white rounded-xl border border-stone-200 p-5"
                    data-testid={`banner-req-${req.request_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-stone-900">{req.title}</p>
                        <p className="text-sm text-stone-500">{req.company_name} | {req.duration_days} {t('giorni', 'days')} | {req.budget}€</p>
                        {req.description && <p className="text-sm text-stone-600 mt-1">{req.description}</p>}
                        {req.link_url && (
                          <a href={req.link_url} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-600 hover:underline mt-1 flex items-center gap-1">
                            <Eye className="h-3 w-3" />{req.link_url}
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                          onClick={() => handleBanner(req.request_id, 'approve')}>
                          <Check className="h-4 w-4 mr-1" />{t('Approva', 'Approve')}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleBanner(req.request_id, 'reject')}>
                          <X className="h-4 w-4 mr-1" />{t('Rifiuta', 'Reject')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* FUND TAB */}
          <TabsContent value="fund">
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <p className="text-sm text-stone-500">{t('Totale raccolto', 'Total collected')}</p>
                <p className="text-2xl font-bold text-emerald-800">
                  €{fundData?.fund_by_status?.completed?.total?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <p className="text-sm text-stone-500">{t('In attesa', 'Pending')}</p>
                <p className="text-2xl font-bold text-amber-600">
                  €{fundData?.fund_by_status?.pending?.total?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <p className="text-sm text-stone-500">{t('Distribuiti', 'Distributed')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  €{fundData?.distributions_by_status?.completed?.total?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>

            <Button data-testid="distribute-fund-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl mb-6"
              onClick={() => setDistributeDialog(true)}>
              <Wallet className="h-4 w-4 mr-2" />{t('Distribuisci fondi', 'Distribute funds')}
            </Button>

            <Dialog open={distributeDialog} onOpenChange={setDistributeDialog}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('Distribuisci fondi a un post', 'Distribute funds to a post')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <Input data-testid="distribute-post-id" placeholder="Post ID (es. post_abc123)"
                    value={distributePostId} onChange={(e) => setDistributePostId(e.target.value)} className="rounded-xl" />
                  <Input data-testid="distribute-amount" type="number" placeholder={t('Importo (€)', 'Amount (€)')} min="1"
                    value={distributeAmount} onChange={(e) => setDistributeAmount(e.target.value)} className="rounded-xl" />
                  <Button data-testid="confirm-distribute" className="w-full bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl"
                    onClick={handleDistribute} disabled={!distributePostId || !distributeAmount}>
                    {t('Conferma distribuzione', 'Confirm distribution')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Recent contributions */}
            <h3 className="font-heading text-lg font-semibold text-stone-900 mb-3">
              {t('Contribuzioni recenti', 'Recent contributions')}
            </h3>
            <div className="space-y-2">
              {fundData?.recent_contributions?.length === 0 ? (
                <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
                  <p className="text-stone-500">{t('Nessuna contribuzione', 'No contributions')}</p>
                </div>
              ) : (
                fundData?.recent_contributions?.map((c, i) => (
                  <div key={i} className="bg-white rounded-xl border border-stone-200 p-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-stone-900">{c.company_name}</p>
                      <p className="text-sm text-stone-500">{new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-800">€{c.amount?.toFixed(2)}</p>
                      <Badge variant="secondary">{c.status}</Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
